import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Field, Input } from "@/components/Ui";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("The two passwords do not match");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 py-16" data-testid="reset-password-page">
      <h1 className="font-display text-3xl font-black tracking-tight text-[#102A43]">
        Set a new password
      </h1>
      <Card className="mt-7">
        {!token ? (
          <p data-testid="reset-missing-token" className="text-sm text-[#B42318]">
            This link is missing its reset token. Please request a new link.
          </p>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#13795B]" />
            <p className="mt-4 text-sm text-[#334e68]">
              Your password has been updated. Redirecting you to sign in…
            </p>
          </div>
        ) : (
          <form onSubmit={submit}>
            <Field label="New password" hint="Minimum 8 characters">
              <Input data-testid="reset-password-input" type="password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Field label="Confirm new password" className="mt-4">
              <Input data-testid="reset-confirm-input" type="password" required minLength={8}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </Field>
            {error && (
              <p data-testid="reset-error" className="mt-4 rounded-[9px] bg-[#fdecec] px-3 py-2 text-sm text-[#B42318]">
                {error}
              </p>
            )}
            <Btn data-testid="reset-submit-btn" loading={busy} className="mt-6 w-full">
              Update password
            </Btn>
          </form>
        )}
        <p className="mt-5 text-center text-sm text-[#667788]">
          <Link to="/forgot-password" className="font-semibold text-[#147D78] underline">
            Request a new reset link
          </Link>
        </p>
      </Card>
    </div>
  );
}
